import React, { useMemo, useCallback, useEffect, useState } from 'react';
import _isEqual from 'lodash/isEqual';
import _startCase from 'lodash/startCase';
import {
    useTable,
    useBlockLayout,
    HeaderGroup,
    useSortBy,
    useRowState,
} from 'react-table';
import { FixedSizeList } from 'react-window';
import { useQuery } from '@apollo/client';

import { queryStrings } from '@trqk/resolvers';
import {
    NumberedItemCell,
    TableHeader,
    TableHeaderGroup,
    TableRow,
} from '@trqk/components';
import { getScrollbarWidth } from '@trqk/utils';

import { DeleteEntityCell, RoleCell } from '.';
import { entityManagerTableDimensions } from '../../constants';

const { GET_SESSION_CURRENT_USER } = queryStrings;
const { rowHeight, tableHeight, defaultColumnWidth } =
    entityManagerTableDimensions;

/**
 * @typedef {EntityManagerTableProps}
 * @property {EntityWithUploadedStatements[]} entities list of entities for the current user
 */
interface EntityManagerTableProps {
    entities: EntityWithUploadedStatements[];
}

/**
 * Renders a table of entities, showing details about each entity including the entity name,
 * role_type, pro, and number of statements uploaded.
 * @param props {EntityManagerTableProps}
 */
export const EntityManagerTable = ({
    entities,
}: EntityManagerTableProps): JSX.Element => {
    /* eslint-disable react/jsx-props-no-spreading */
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id ?? null;

    /* BEGIN UI state logic */

    const [data, setData] = useState(entities);

    useEffect((): void => {
        if (!_isEqual(entities, data)) {
            setData(entities);
        }
    }, [entities, data, setData]);

    const deleteTableRowByIndex: DeleteTableRowByIndex = useCallback(
        (index: number): void => {
            const nextData = [...data];

            nextData.splice(index, 1);

            setData(nextData);
        },
        [data],
    );

    /* END UI state logic */

    /* BEGIN table management logic */

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const columns = useMemo(
        () => [
            {
                id: 'name',
                accessor: ({ name }: EntityWithUploadedStatements): string => {
                    if (name === null) {
                        return 'None';
                    }

                    return _startCase(name);
                },
                Cell: NumberedItemCell,
                headerLabel: 'Entity',
                Header: TableHeader,
                width: 450,
            },
            {
                id: 'role_type',
                accessor: ({
                    role_type,
                }: EntityWithUploadedStatements): string => {
                    if (role_type === null || role_type === 'unknown') {
                        return '';
                    }

                    return role_type;
                },
                Cell: RoleCell,
                headerLabel: 'Role',
                Header: TableHeader,
                width: 120,
            },
            {
                id: 'pro',
                accessor: ({
                    pro,
                }: EntityWithUploadedStatements): Maybe<string> => pro ?? null,
                headerLabel: 'PRO',
                Header: TableHeader,
                width: 120,
            },
            {
                id: 'uploadedStatements',
                accessor: ({
                    uploadedStatements,
                }: EntityWithUploadedStatements): number => uploadedStatements,
                headerLabel: 'Uploaded Statements',
                Header: TableHeader,
                width: 200,
            },
            {
                id: 'delete',
                accessor: ({
                    id,
                    ROW_LOCK,
                }: EntityWithUploadedStatements): {
                    entityId: string;
                    userId: Maybe<string>;
                    ROW_LOCK?: Maybe<string>;
                } => ({
                    entityId: id,
                    userId,
                    ROW_LOCK,
                }),
                Cell: DeleteEntityCell,
                width: 50,
                deleteTableRowByIndex,
            },
        ],
        [userId, deleteTableRowByIndex],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        totalColumnsWidth,
        prepareRow,
    } = useTable<EntityWithUploadedStatements>(
        {
            columns,
            data,
            defaultColumn,
            initialState: {
                sortBy: [
                    {
                        id: 'name',
                        desc: false,
                    },
                ],
            },
        },
        useBlockLayout,
        useSortBy,
        useRowState,
    );

    /* END table management logic */

    const RenderRow = useCallback(
        ({ index, style }) => {
            const row = rows[index];

            prepareRow(row);

            return (
                <TableRow<EntityWithUploadedStatements>
                    index={index}
                    style={style}
                    row={row}
                    textSize="sm"
                />
            );
        },
        [prepareRow, rows],
    );

    const scrollbarWidth = useMemo(() => getScrollbarWidth(), []);

    return (
        <div {...getTableProps()} className="trqk-table text-sm font-normal">
            <div>
                {headerGroups.map(
                    (
                        headerGroup: HeaderGroup<EntityWithUploadedStatements>,
                    ): JSX.Element => {
                        const { key: headerGroupKey } =
                            headerGroup.getHeaderGroupProps();

                        return (
                            <TableHeaderGroup<EntityWithUploadedStatements>
                                key={headerGroupKey}
                                headerGroup={headerGroup}
                            />
                        );
                    },
                )}
            </div>
            <div {...getTableBodyProps()}>
                <FixedSizeList
                    height={tableHeight}
                    itemCount={rows.length}
                    itemSize={rowHeight}
                    width={totalColumnsWidth + scrollbarWidth}
                >
                    {RenderRow}
                </FixedSizeList>
            </div>
        </div>
    );
};
